<template>
	<div class="pronto-input" :class="{ focused, small, large }">
		<div class="before">
			<slot name="before">
				<div class="icon" v-if="icon">
					<component :is="icon" v-bind="iconProps" />
				</div>
			</slot>
		</div>
		<input
			v-model="value"
			class="input"
			:disabled="disabled"
			:readonly="readonly"
			:maxlength="maxlength"
			:placeholder="placeholder"
			:role="role"
			:aria-owns="ariaOwns"
			:type="type"
			:id="labelId || null"
			ref="input"
			@focus="onFocus"
			@blur="onBlur"
			@keyup.enter="$emit('enter', $event)"
		/>
		<div class="after">
			<slot name="after" />
			<CloseIcon class="clear" v-if="allowClear && value" @click="clear" />
		</div>
	</div>
</template>

<script>
import { CloseIcon } from '@/icons'
import { nextTick } from 'vue'
export default {
	name: 'Input',
	components: { CloseIcon },
	props: {
		modelValue: null,
		placeholder: String,
		disabled: { type: Boolean, default: null },
		readonly: { type: Boolean, default: null },
		autoFocus: { type: Boolean, default: null },
		selectAll: { type: Boolean, default: null },
		allowClear: { type: Boolean, default: null },
		small: { type: Boolean, default: null },
		large: { type: Boolean, default: null },
		role: String,
		ariaOwns: String,
		type: {
			type: String,
			default: 'text',
		},
		maxlength: [Number, String],
		icon: Object,
		iconProps: Object,
	},
	inject: {
		labelId: {
			from: 'labelId',
			default: null,
		},
	},
	data() {
		return { value: this.modelValue || null, focused: false }
	},
	async mounted() {
		if (this.autoFocus && !this.accessibilityMode) {
			await nextTick()
			this.focus()
		}
		if (this.selectAll) {
			await nextTick()
			this.select()
		}
	},
	emits: ['update:modelValue', 'change', 'enter', 'focus', 'blur'],
	watch: {
		modelValue(modelValue) {
			// if prop value changes, update internal value model
			this.value = modelValue
		},
		value(val) {
			// if internal value model changes, alert parent of change
			this.$emit('update:modelValue', val)
			this.$emit('change', val) // additional event to handle v2/v3 backwards compatibility
		},
	},
	methods: {
		onFocus(e) {
			this.focused = true
			this.$emit('focus', e)
		},
		onBlur(e) {
			this.focused = false
			this.$emit('blur', e)
		},
		focus() {
			this.$refs.input.focus()
		},
		select() {
			this.$refs.input.select()
		},
		clear() {
			this.value = null
		},
		setSelectionRange(start, end) {
			this.$refs.input.setSelectionRange(start, end)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$height: 40px;
$height-sm: 30px;
$height-lg: 60px;

.pronto-input {
	width: 100%;
	display: flex;
	align-items: center;
	@include radius;
	border: 1px solid var(--neutral-200);
	overflow: hidden;
	height: $height;
	font-size: 16px;
	color: var(--neutral-800);
	background: #fafafa;
	> .input {
		width: 100%;
		padding: 8px 16px;
		flex-grow: 1;
		border: 0;
		font-size: inherit;
		color: inherit;
		background-color: inherit;
		font-family: Avenir-Roman;
		&:active,
		&:focus {
			outline: none;
		}
		&::placeholder {
			color: var(--neutral-400);
		}
		&[disabled='disabled'] {
			opacity: 0.7;
			cursor: not-allowed;
		}
	}
	&.focused {
		border-color: var(--blue-300);
	}
	&.form-item-success {
		border-color: var(--teal-300);
	}
	&.form-item-error {
		border-color: var(--red-200);
		&.focused {
			border-color: var(--red-200);
		}
	}
	&.small {
		height: $height-sm;
		font-size: 14px;
		> .input {
			padding: 8px 12px;
		}
		.icon {
			margin-left: 12px;
		}
	}
	&.large {
		height: $height-lg;
	}
	.before,
	.after {
		display: flex;
		align-items: center;
	}
	.clear {
		cursor: pointer;
		padding-right: 10px;
		font-size: 22px;
		&:hover {
			color: black;
		}
	}
	.icon {
		display: flex;
		align-items: center;
		margin-left: 16px;
		color: var(--neutral-400);
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pronto-input {
		background: var(--neutral-100);
		&.focused {
			border-color: var(--teal-300);
		}
	}
}
</style>
